import useCustomMutation from "@/composables/useCustomMutation";
import { CostCenter } from "@/interfaces/costCenter";
import { CustomAxiosRequestConfig } from "@/interfaces/customAxiosRequestConfig";
import { MutationParams } from "@/interfaces/mutationParams";
import type { PaginatedResponse } from "@/interfaces/paginatedResponse";
import axios from "@/lib/axios";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { get } from "@vueuse/core";
import { type MaybeRef, computed } from "vue";
import { useToast } from "vue-toastification";

interface Params {
  companyId: number;
  costCenterId?: number;
  // Filtros server-side: search hace LIKE en name o key; department filtra
  // por department_id; page/perPage paginación estándar Laravel (default 10).
  search?: MaybeRef<string>;
  department?: MaybeRef<number | null>;
  page?: MaybeRef<number>;
  perPage?: number;
}

interface ListParams {
  companyId: number;
  search?: string;
  department?: number | null;
  page?: number;
  perPage?: number;
}

interface CreateParams {
  name: string;
  key: string;
  companyId: number;
  departmentId: number;
}

const getCompanyCostCenters = async (
  params: ListParams,
): Promise<PaginatedResponse<CostCenter>> => {
  const { data } = await axios.get<PaginatedResponse<CostCenter>>(
    `/api/companies/${params.companyId}/cost-centers`,
    {
      params: {
        search: params.search || undefined,
        department: params.department || undefined,
        page: params.page,
        per_page: params.perPage,
      },
      errorHandling: false,
    } as CustomAxiosRequestConfig,
  );
  return data;
};

const createCostCenter = async (params: CreateParams) => {
  const { data } = await axios.post(
    `/api/companies/${params.companyId}/cost-centers`,
    params,
  );
  return data;
};

const useCompanyCostCenter = (params?: Params) => {
  const toast = useToast();
  const createMutation = useMutation(
    ({ params }: MutationParams<CreateParams>) => createCostCenter(params),
    useCustomMutation(toast, "create"),
  );

  const search = computed(() => get(params?.search) ?? "");
  const department = computed(() => get(params?.department) ?? null);
  const page = computed(() => get(params?.page) ?? 1);
  // El backend SIEMPRE pagina (default 10). Consumidores legacy
  // (CompanyTabPanel) esperan la lista completa: sin perPage explícito se
  // pide 100 para no truncarlos a la primera página.
  const perPage = params?.perPage ?? 100;

  const {
    data: response,
    isLoading,
    isFetching,
    isError,
  } = useQuery(
    // companyId como segundo elemento: las invalidaciones existentes usan el
    // prefijo ["companyCostCenter", companyId] y deben seguir alcanzando esta key.
    [
      "companyCostCenter",
      params?.companyId,
      params?.costCenterId,
      search,
      department,
      page,
      perPage,
    ],
    () =>
      getCompanyCostCenters({
        companyId: params!.companyId,
        search: search.value,
        department: department.value,
        page: page.value,
        perPage,
      }),
    {
      enabled: !!params,
      retry: false,
      // Mantiene la página anterior visible mientras carga la nueva:
      // sin saltos de layout al paginar o filtrar.
      keepPreviousData: true,
    },
  );

  const data = computed(() => response.value?.data ?? []);
  const meta = computed(() => response.value?.meta ?? null);

  return {
    data,
    meta,
    isError,
    isLoading,
    isFetching,
    createMutation,
    createCompanyCostCenter: createMutation.mutate,
    createCompanyCostCenterAsync: createMutation.mutateAsync,
    createCostCenterLoading: createMutation.isLoading,
  };
};

export default useCompanyCostCenter;
